import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The RefreshButton's timing (saved-moment handoff §5), as a hook:
 *
 *   idle ↻ → pressed (0.9×, 80 ms) → working → done (green ✓, 1 s) → idle
 *
 *   - working appears only after 150 ms, so a fast refresh never flashes a
 *     spinner for a frame, and once shown it holds for at least 400 ms, so a
 *     slow one never looks stuck;
 *   - pressing again while working restarts the job (the button stays
 *     enabled); the earlier job's result is ignored;
 *   - failed stays until the next press.
 */
export type RefreshPhase = "idle" | "working" | "done" | "failed";

export const WORKING_DELAY_MS = 150;
export const WORKING_MIN_MS = 400;
export const DONE_MS = 1000;
/** A job with no promise and no busy signal is treated as done after this. */
const UNTRACKED_JOB_MS = 300;

export interface RefreshSequence {
  phase: RefreshPhase;
  /** Increments per press, to replay the press animation. */
  pressId: number;
  run: () => void;
}

/**
 * @param job   the refresh. A returned promise is tracked: it resolving is
 *              done, it rejecting is failed.
 * @param busy  for surfaces whose work is not awaitable from the click (the
 *              job only starts it): true while that work is in flight. The
 *              job then ends when busy goes back to false.
 */
export function useRefreshSequence(job: () => unknown, busy?: boolean): RefreshSequence {
  const [phase, setPhase] = useState<RefreshPhase>("idle");
  const [pressId, setPressId] = useState(0);

  const gen = useRef(0);
  const shownAt = useRef<number | null>(null);
  const showTimer = useRef<number | null>(null);
  const finishTimer = useRef<number | null>(null);
  const doneTimer = useRef<number | null>(null);
  const jobRef = useRef(job);
  jobRef.current = job;

  // A job that is only "busy"-tracked: resolved by the effect below.
  const busyJob = useRef<{ gen: number; sawBusy: boolean; resolve: () => void } | null>(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;

  const clear = (r: { current: number | null }) => {
    if (r.current !== null) window.clearTimeout(r.current);
    r.current = null;
  };

  const finish = useCallback((g: number, ok: boolean) => {
    if (g !== gen.current) return; // superseded by a later press
    clear(showTimer);
    const settle = () => {
      if (g !== gen.current) return;
      shownAt.current = null;
      setPhase(ok ? "done" : "failed");
      if (ok) doneTimer.current = window.setTimeout(() => setPhase("idle"), DONE_MS);
    };
    if (shownAt.current !== null) {
      const left = shownAt.current + WORKING_MIN_MS - Date.now();
      if (left > 0) {
        finishTimer.current = window.setTimeout(settle, left);
        return;
      }
    }
    settle();
  }, []);

  const run = useCallback(() => {
    const g = ++gen.current;
    setPressId((n) => n + 1);
    clear(doneTimer);
    clear(finishTimer);
    // Already showing working: a restart keeps it up rather than flickering.
    if (shownAt.current === null) {
      clear(showTimer);
      setPhase("idle");
      showTimer.current = window.setTimeout(() => {
        if (g !== gen.current) return;
        shownAt.current = Date.now();
        setPhase("working");
      }, WORKING_DELAY_MS);
    }

    let result: unknown;
    try {
      result = jobRef.current();
    } catch {
      finish(g, false);
      return;
    }
    if (result && typeof (result as PromiseLike<unknown>).then === "function") {
      (result as PromiseLike<unknown>).then(
        () => finish(g, true),
        () => finish(g, false),
      );
      return;
    }
    // Not awaitable. With a busy signal, wait for it; without, a short beat.
    if (busyRef.current !== undefined) {
      busyJob.current = { gen: g, sawBusy: Boolean(busyRef.current), resolve: () => finish(g, true) };
      window.setTimeout(() => {
        const b = busyJob.current;
        if (b && b.gen === g && !b.sawBusy) {
          busyJob.current = null;
          finish(g, true);
        }
      }, UNTRACKED_JOB_MS);
    } else {
      window.setTimeout(() => finish(g, true), UNTRACKED_JOB_MS);
    }
  }, [finish]);

  useEffect(() => {
    const b = busyJob.current;
    if (!b) return;
    if (busy) b.sawBusy = true;
    else if (b.sawBusy) {
      busyJob.current = null;
      b.resolve();
    }
  }, [busy]);

  useEffect(
    () => () => {
      gen.current += 1;
      clear(showTimer);
      clear(finishTimer);
      clear(doneTimer);
    },
    [],
  );

  return { phase, pressId, run };
}
